import { useRef, useState, useEffect } from "react";
import type { Note } from "@/types";
import { NOTE_COLORS, NOTE_W, NOTE_BODY_H, getNoteColors } from "@/constants";

type NoteState = "idle" | "focused" | "editing";

export function StickyNoteCard({
  note,
  scaleRef,
  selected,
  autoFocus,
  onSelect,
  onDragStart,
  onMove,
  onSavePosition,
  onContentChange,
  onColorChange,
}: {
  note: Note;
  scaleRef: React.RefObject<number>;
  selected: boolean;
  autoFocus?: boolean;
  onSelect: (id: string) => void;
  onDragStart?: (id: string) => void;
  onMove: (id: string, x: number, y: number) => void;
  onSavePosition: (id: string, x: number, y: number) => void;
  onContentChange: (id: string, content: string) => void;
  onColorChange: (id: string, color: string) => void;
}) {
  const [noteState, setNoteState] = useState<NoteState>(autoFocus ? "editing" : "idle");
  const noteStateRef = useRef<NoteState>(autoFocus ? "editing" : "idle");
  const [lifted, setLifted] = useState(false);
  const dragging = useRef(false);
  const didDrag = useRef(false);
  const wasIdle = useRef(false);
  const lastMouse = useRef({ x: 0, y: 0 });
  const noteRef = useRef(note);
  noteRef.current = note;
  const onMoveRef = useRef(onMove);
  onMoveRef.current = onMove;
  const onSaveRef = useRef(onSavePosition);
  onSaveRef.current = onSavePosition;
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const updateState = (s: NoteState) => {
    noteStateRef.current = s;
    setNoteState(s);
  };

  // Derived-state reset: when the parent deselects this note, snap back to idle
  // synchronously during render (rather than waiting for a useEffect) so the
  // ref is immediately correct for the next pointer event.
  if (!selected && noteState !== "idle") {
    noteStateRef.current = "idle";
    setNoteState("idle");
  }

  // Always derive the effective visual state from BOTH the prop and local state.
  // This guarantees isFocused/isEditing are false the instant selected goes false,
  // even in the current render before the setState above takes effect.
  const effectiveState: NoteState = selected ? noteState : "idle";

  const autoResize = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = el.scrollHeight + "px";
  };

  useEffect(() => { autoResize(); }, [note.content]);

  useEffect(() => {
    if (autoFocus) {
      onSelect(note._id);
      setTimeout(() => textareaRef.current?.focus(), 0);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const state = noteStateRef.current;
    if (state === "editing") return;
    e.stopPropagation();
    onSelect(note._id);
    onDragStart?.(note._id);

    wasIdle.current = state === "idle";
    dragging.current = true;
    didDrag.current = false;
    lastMouse.current = { x: e.clientX, y: e.clientY };
    e.currentTarget.setPointerCapture(e.pointerId);
    setLifted(true);

    if (state === "idle") updateState("focused");
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return;
    const s = scaleRef.current ?? 1;
    const dx = (e.clientX - lastMouse.current.x) / s;
    const dy = (e.clientY - lastMouse.current.y) / s;
    if (Math.abs(dx) > 1 || Math.abs(dy) > 1) didDrag.current = true;
    lastMouse.current = { x: e.clientX, y: e.clientY };
    const n = noteRef.current;
    onMoveRef.current(n._id, n.x + dx, n.y + dy);
  };

  const onPointerUp = () => {
    if (!dragging.current) return;
    dragging.current = false;
    setLifted(false);

    if (didDrag.current) {
      const n = noteRef.current;
      onSaveRef.current(n._id, n.x, n.y);
    } else if (!wasIdle.current) {
      updateState("editing");
      setTimeout(() => textareaRef.current?.focus(), 0);
    }
  };

  const colors = getNoteColors(note.color);
  const isEditing = effectiveState === "editing";
  const isFocused = effectiveState !== "idle";

  return (
    <div
      style={{
        position: "absolute",
        left: note.x,
        top: note.y,
        width: NOTE_W,
        zIndex: 5,
        pointerEvents: "auto",
        filter: lifted
          ? "drop-shadow(0 12px 28px rgba(0,0,0,0.28))"
          : "drop-shadow(0 4px 12px rgba(0,0,0,0.15))",
        transform: lifted ? "scale(1.04)" : "scale(1)",
        transition: lifted
          ? "transform 0.08s ease-out, filter 0.08s ease-out"
          : "transform 0.18s ease-out, filter 0.18s ease-out",
        outline: isFocused ? "2px solid #0091ff" : "2px solid transparent",
        outlineOffset: 2,
        borderRadius: 8,
        cursor: isEditing ? "default" : lifted ? "grabbing" : isFocused ? "grab" : "pointer",
        touchAction: "none",
        userSelect: isEditing ? "text" : "none",
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
    >
      {isFocused && (
        <div
          style={{
            position: "absolute",
            bottom: "calc(100% + 10px)",
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            alignItems: "center",
            gap: 5,
            padding: "6px 10px",
            background: "white",
            border: "1px solid rgba(0,0,0,0.10)",
            borderRadius: 10,
            boxShadow: "0 4px 16px rgba(0,0,0,0.13), 0 1px 4px rgba(0,0,0,0.07)",
            pointerEvents: "auto",
            whiteSpace: "nowrap",
            zIndex: 20,
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {NOTE_COLORS.map((c) => (
            <button
              key={c.body}
              onClick={(e) => { e.stopPropagation(); onColorChange(note._id, c.body); }}
              style={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                border: note.color === c.body
                  ? "2.5px solid #0091ff"
                  : "1.5px solid rgba(0,0,0,0.18)",
                background: c.body,
                cursor: "pointer",
                padding: 0,
                flexShrink: 0,
                boxSizing: "border-box",
                outline: "none",
                boxShadow: note.color === c.body ? "0 0 0 1.5px white inset" : "none",
              }}
            />
          ))}
        </div>
      )}
      <div style={{ background: colors.body, borderRadius: 8, padding: "8px 10px 10px" }}>
        <textarea
          ref={textareaRef}
          value={note.content}
          onChange={(e) => { onContentChange(note._id, e.target.value); autoResize(); }}
          placeholder="Write something..."
          rows={1}
          style={{
            width: "100%",
            minHeight: NOTE_BODY_H,
            border: "none",
            background: "transparent",
            resize: "none",
            overflow: "hidden",
            outline: "none",
            fontFamily: "inherit",
            fontSize: 13,
            lineHeight: 1.55,
            color: "#1c1917",
            boxSizing: "border-box",
            display: "block",
            cursor: isEditing ? "text" : "inherit",
            pointerEvents: isEditing ? "auto" : "none",
          }}
          onFocus={() => { onSelect(note._id); updateState("editing"); }}
          onBlur={() => updateState("focused")}
          onMouseDown={(e) => { if (isEditing) e.stopPropagation(); }}
          onMouseUp={(e) => e.stopPropagation()}
        />
      </div>
    </div>
  );
}
